import { z } from "zod";

import { isValidEmail, isValidPhone, normalizeEmail, normalizePhone } from "@pharmacy/domain";

/**
 * Pharmacy signup — the only flow in the system that runs before a tenant
 * exists, and the only unauthenticated surface.
 *
 * Three steps, deliberately:
 *
 *   1. start    phone or email in, code out (to the console in development)
 *   2. verify   code in, a short-lived proof token out
 *   3. complete token plus pharmacy details in, a real account out
 *
 * Splitting verification from account creation means a mistyped number is
 * caught in seconds rather than after somebody has filled in a whole form, and
 * it keeps the expensive step behind proof that the phone is real.
 */

/**
 * Every phone entering the system is normalised here, at the edge.
 *
 * `+252 61 555 0100`, `0615550100` and `615550100` are the same subscriber, and
 * a globally unique phone number means nothing if three spellings can each own
 * an account. Transforming in the schema means no handler can forget.
 */
export const phoneSchema = z
  .string()
  .trim()
  .min(6, "Enter a phone number")
  .refine((v) => isValidPhone(v), "That is not a valid phone number")
  .transform((v) => normalizePhone(v)!);

/** Lower-case and trimmed at the edge, like a phone, so one address is one account. */
export const emailSchema = z
  .string()
  .trim()
  .min(3, "Enter an email")
  .refine((v) => isValidEmail(v), "That is not a valid email")
  .transform((v) => normalizeEmail(v)!);

/**
 * Where a code goes: a phone OR an email, never both and never neither.
 *
 * Owners may sign up, sign in and recover with either. The fields are spread
 * into each request that takes one, and `oneTarget` refuses a request with
 * both or neither.
 */
export const phoneOrEmail = {
  phone: phoneSchema.optional(),
  email: emailSchema.optional(),
};

export const oneTarget = <T extends { phone?: string; email?: string }>(v: T) =>
  (v.phone ? 1 : 0) + (v.email ? 1 : 0) === 1;

export const ONE_TARGET = { message: "Enter a phone number or an email" };

/** Where a code was sent, shown back so a typo is obvious: "+252 61 *** 0100", "fa***@gmail.com". */
const sentTo = {
  channel: z.enum(["phone", "email"]),
  /** Partly hidden. */
  sentTo: z.string(),
};

/** Six digits. Long enough to resist guessing at the attempt limits we allow. */
export const otpCodeSchema = z
  .string()
  .trim()
  .regex(/^\d{6}$/, "Enter the 6-digit code");

/**
 * Admin password policy.
 *
 * Length is the control that matters; character-class rules mostly produce
 * `Password1!` and a sticky note. Ten characters is the floor, and the
 * commonest passwords are refused outright.
 */
export const passwordSchema = z
  .string()
  .min(8, "At least 8 characters")
  .max(200, "Too long")
  .refine(
    (v) => !["password12", "1234567890", "qwertyuiop"].includes(v.toLowerCase()),
    "That password is too easy to guess",
  );

/* ------------------------------------------------------------ step 1 */

export const signupStartRequest = z.object(phoneOrEmail).refine(oneTarget, ONE_TARGET);

export const signupStartResponse = z.object({
  ...sentTo,
  /** For a phone: the same as `sentTo`. Kept for apps from before email. */
  maskedPhone: z.string().optional(),
  expiresInSeconds: z.number().int().positive(),
  /**
   * Present only outside production. The console sender puts the code here so
   * the mobile app can show it during development, and so tests can read it
   * without scraping logs.
   */
  devCode: z.string().optional(),
});

/* ------------------------------------------------------------ step 2 */

export const signupVerifyRequest = z
  .object({ ...phoneOrEmail, code: otpCodeSchema })
  .refine(oneTarget, ONE_TARGET);

export const signupVerifyResponse = z.object({
  /** Short-lived proof that this phone or email was verified. Spent by step 3. */
  verificationToken: z.string(),
  expiresInSeconds: z.number().int().positive(),
});

/* ------------------------------------------------------------ step 3 */

export const signupCompleteRequest = z.object({
  verificationToken: z.string().min(1),

  pharmacyName: z.string().trim().min(2, "Enter the pharmacy name").max(120),
  /** The first branch. Most shops have exactly one and never think about it. */
  branchName: z.string().trim().min(1).max(120).default("Main"),

  ownerName: z.string().trim().min(2, "Enter your name").max(120),
  /**
   * For somebody who signed up with a PHONE: an email to reach them by,
   * optional, and not a way in until they prove it from Profile.
   */
  email: emailSchema.optional(),
  /**
   * For somebody who signed up with an EMAIL: the pharmacy's phone, required,
   * so the shop can be reached. Not checked with a code, so it is contact
   * detail only — not a way in until they prove it from Profile.
   */
  contactPhone: phoneSchema.optional(),

  password: passwordSchema,
});

export const signupCompleteResponse = z.object({
  tenantId: z.string().uuid(),
  branchId: z.string().uuid(),
  userId: z.string().uuid(),
  pharmacyName: z.string(),
});

export type SignupStartRequest = z.infer<typeof signupStartRequest>;
export type SignupStartResponse = z.infer<typeof signupStartResponse>;
export type SignupVerifyRequest = z.infer<typeof signupVerifyRequest>;
export type SignupVerifyResponse = z.infer<typeof signupVerifyResponse>;
export type SignupCompleteRequest = z.infer<typeof signupCompleteRequest>;
export type SignupCompleteResponse = z.infer<typeof signupCompleteResponse>;
